import { JOB_PHASES } from "./generated/navigation-contract.js";

/**
 * Queueing is not execution, and the UI has to say so.
 *
 * The difference between a user thinking "nothing happened" and understanding
 * "it is queued" is one sentence, so that sentence lives in exactly one place
 * and every surface — the request page, the run page, the alert intent outbox —
 * renders through it.
 */

/** Mirrors the server's `RunState` in `domain-service.ts`. */
export type RunState = "queued" | "running" | "waiting" | "succeeded" | "failed" | "cancelled";

/** Mirrors the server's `RequestState` in `domain-service.ts`. */
export type RequestState = "pending" | "accepted" | "completed" | "cancelled" | "failed";

/**
 * `waiting` covers everything accepted but not executing, `active` is work
 * genuinely underway, `settled` is finished for better or worse. Nothing that
 * is merely enqueued is ever `active` — that is the whole point of the split.
 */
export type Phase = "waiting" | "active" | "settled";

export interface PhaseCopy {
  readonly phase: Phase;
  readonly headline: string;
  readonly detail: string;
  /** True when the user should be told this needs them, not just watched. */
  readonly needsAttention: boolean;
}

/**
 * The recorded cause, trimmed of the trailing punctuation the copy supplies.
 *
 * A server that has not shipped the joined field, and a run that failed before
 * it stored a result, both arrive here as nothing — so the reader needs an explicit
 * missing-reason state.
 */
function recordedCause(failureDetail?: string | null): string {
  return (failureDetail ?? "").trim().replace(/[.\s]+$/, "");
}

export function runPhase(state: RunState, waitReason?: string | null, failureDetail?: string | null): PhaseCopy {
  switch (state) {
    case "queued":
      return {
        phase: "waiting",
        headline: "Queued",
        detail: "Accepted and waiting its turn. Nothing has run yet.",
        needsAttention: false,
      };
    case "running":
      return {
        phase: "active",
        headline: "Running now",
        detail: "Work is underway. Refresh to check its progress.",
        needsAttention: false,
      };
    case "waiting":
      return {
        phase: "waiting",
        headline: "Paused",
        detail: recordedCause(waitReason)
          ? `Waiting on ${recordedCause(waitReason)}. Nothing is running right now.`
          : "Waiting on something else. Nothing is running right now.",
        needsAttention: false,
      };
    case "succeeded":
      return {
        phase: "settled",
        headline: "Finished",
        detail: "The run finished successfully.",
        needsAttention: false,
      };
    case "failed": {
      const cause = recordedCause(failureDetail);
      return {
        phase: "settled",
        headline: "Failed",
        detail: cause === ""
          ? "No failure reason was recorded."
          : `It failed with: ${cause}.`,
        needsAttention: true,
      };
    }
    case "cancelled":
      return {
        phase: "settled",
        headline: "Cancelled",
        detail: "The run stopped before it finished.",
        needsAttention: false,
      };
  }
}

export function requestPhase(state: RequestState): PhaseCopy {
  switch (state) {
    case "pending":
      return {
        phase: "waiting",
        headline: "Queued",
        detail: "Your request is recorded. No run has started yet.",
        needsAttention: false,
      };
    case "accepted":
      return {
        phase: "active",
        headline: "Accepted",
        detail: "A run has been created for this request.",
        needsAttention: false,
      };
    case "completed":
      return {
        phase: "settled",
        headline: "Completed",
        detail: "Open the result this request produced.",
        needsAttention: false,
      };
    case "cancelled":
      return {
        phase: "settled",
        headline: "Cancelled",
        detail: "This request was stopped before it completed.",
        needsAttention: false,
      };
    case "failed":
      return {
        phase: "settled",
        headline: "Failed",
        detail: "Open the run that failed to see the log and retry.",
        needsAttention: true,
      };
  }
}

export type JobVerdict = "ok" | "failed" | "answered_unconsumed" | "quiet" | "overdue";

/**
 * A job's last verdict, in the reader's vocabulary. The server drew it from
 * the ledger, not from the job's own claim, and each non-ok one names the
 * next thing to check. `null` is a job that has started and never finished.
 */
export function jobPhase(verdict: JobVerdict | null): PhaseCopy {
  return JOB_PHASES[verdict ?? "running"];
}

/**
 * The same honesty applied to anything this client has enqueued but not yet
 * handed to the server. An unsent intent is never described in the past tense.
 */
export function outboxPhase(sent: boolean, failure?: string): PhaseCopy {
  if (failure) {
    return { phase: "settled", headline: "Not sent", detail: failure, needsAttention: true };
  }
  return sent
    ? { phase: "settled", headline: "Sent", detail: "The server has this decision.", needsAttention: false }
    : { phase: "waiting", headline: "Queued", detail: "Saved on this device, not sent yet.", needsAttention: false };
}
